/**
 * An output's own menu (07 Workshop, row 37): Open beside, Rename, Download
 * (its formats beside it) and Archive. Archive is not permanent: Show archived
 * brings it back. On a phone the same actions rise as a sheet.
 */
import { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  SubmenuTrigger,
  type Key,
} from "react-aria-components";
import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  ChevronDown,
  Download,
  Ellipsis,
  FileDown,
  MoreHorizontal,
  PanelRight,
  Pencil,
} from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { cn } from "@/utils/helpers";
import { KindTag, StateTag } from "./WorkshopParts";

export type ExportFormat = "md" | "docx" | "pdf";

export const EXPORT_FORMATS: { id: ExportFormat; label: string }[] = [
  { id: "md", label: "Markdown (.md)" },
  { id: "docx", label: "Word (.docx)" },
  { id: "pdf", label: "PDF" },
];

type Props = {
  title: string;
  tag: string | null;
  meta: string;
  archived: boolean;
  /** Phone: the actions rise as a sheet. */
  asSheet: boolean;
  onOpen: () => void;
  /** Desk only: the pane takes the chat's width too. */
  onOpenFull?: () => void;
  /** Documents only: ideas have no export. */
  onExport?: (format: ExportFormat) => void;
  /** Omitted when the viewer cannot change the project. */
  onArchive?: (archived: boolean) => void;
  /** Documents only, for someone who can change the project. */
  onRename?: () => void;
};

/**
 * "lifecycle" is Archive or Restore by the output's state. One stable id: an
 * item's id must not change while the menu closes (the row's state flips at
 * once, optimistically, and react-aria refuses a changed id).
 */
type Action = "open" | "full" | "rename" | "lifecycle" | "archive" | "restore" | `export:${ExportFormat}`;

export default function WorkshopOutputMenu({
  title,
  tag,
  meta,
  archived,
  asSheet,
  onOpen,
  onOpenFull,
  onExport,
  onArchive,
  onRename,
}: Props) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const portal = useCoShellPortal();
  const label = `Actions for ${title}`;

  const run = (action: Action) => {
    if (action === "open") onOpen();
    else if (action === "full") onOpenFull?.();
    else if (action === "rename") onRename?.();
    else if (action === "lifecycle") onArchive?.(!archived);
    else if (action === "archive") onArchive?.(true);
    else if (action === "restore") onArchive?.(false);
    else onExport?.(action.slice("export:".length) as ExportFormat);
  };

  if (asSheet) {
    const choose = (action: Action) => {
      setSheetOpen(false);
      setExportOpen(false);
      run(action);
    };
    return (
      <>
        <button
          type="button"
          className="ws-card-menu-trigger ws-orow-more"
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => setSheetOpen(true)}
        >
          <MoreHorizontal className="size-4" strokeWidth={2} aria-hidden="true" />
        </button>
        <KitModal
          isOpen={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) setExportOpen(false);
          }}
          aria-label={label}
          hideCloseButton
          modalClassName="ws-dialog ws-action-sheet"
          overlayClassName="ws-dialog-overlay"
        >
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <div className="ws-action-sheet-head ws-output-sheet-head">
            <p className="ws-orow-tags">
              {tag ? <KindTag>{tag}</KindTag> : null}
              {archived ? <StateTag>Archived</StateTag> : null}
              {meta ? <span className="ws-meta">{meta}</span> : null}
            </p>
            <p className="ws-output-sheet-title">{title}</p>
          </div>
          <div className="ws-action-sheet-list">
            <button type="button" className="ws-action-sheet-item" onClick={() => choose("open")}>
              <ArrowUpRight className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
              Open
            </button>
            {onRename ? (
              <button type="button" className="ws-action-sheet-item" onClick={() => choose("rename")}>
                <Pencil className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
                Rename
              </button>
            ) : null}
            {onExport ? (
              <>
                <button
                  type="button"
                  className="ws-action-sheet-item"
                  aria-expanded={exportOpen}
                  onClick={() => setExportOpen((v) => !v)}
                >
                  <Download className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
                  Download
                  <ChevronDown
                    className={cn("ms-auto size-4 transition-transform", exportOpen && "rotate-180")}
                    strokeWidth={1.8}
                    aria-hidden="true"
                  />
                </button>
                {exportOpen
                  ? EXPORT_FORMATS.map((format) => (
                      <button
                        key={format.id}
                        type="button"
                        className="ws-action-sheet-item ws-action-sheet-item--sub"
                        onClick={() => choose(`export:${format.id}`)}
                      >
                        {format.label}
                      </button>
                    ))
                  : null}
              </>
            ) : null}
            {onArchive ? (
              <>
                <span className="ws-action-sheet-sep" aria-hidden="true" />
                <button
                  type="button"
                  className="ws-action-sheet-item"
                  onClick={() => choose(archived ? "restore" : "archive")}
                >
                  {archived ? (
                    <ArchiveRestore className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
                  ) : (
                    <Archive className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
                  )}
                  {archived ? "Restore" : "Archive"}
                </button>
              </>
            ) : null}
          </div>
        </KitModal>
      </>
    );
  }

  return (
    <MenuTrigger>
      <Button aria-label={label} className="ws-more ws-orow-more">
        <Ellipsis aria-hidden="true" />
      </Button>
      <Popover
        placement="bottom end"
        offset={6}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="ws-menu-pop"
      >
        <Menu aria-label={label} className="ws-menu-list" onAction={(key: Key) => run(key as Action)}>
          <MenuItem id="open" textValue="Open beside" className="ws-menu-row">
            <PanelRight className="ws-menu-row-icon" aria-hidden="true" />
            <span className="ws-menu-row-label">Open beside</span>
          </MenuItem>
          {onRename ? (
            <MenuItem id="rename" textValue="Rename" className="ws-menu-row">
              <Pencil className="ws-menu-row-icon" aria-hidden="true" />
              <span className="ws-menu-row-label">Rename</span>
            </MenuItem>
          ) : null}
          {onExport ? (
            <SubmenuTrigger>
              <MenuItem textValue="Download" className="ws-menu-row">
                <Download className="ws-menu-row-icon" aria-hidden="true" />
                <span className="ws-menu-row-label">Download</span>
              </MenuItem>
              <Popover
                UNSTABLE_portalContainer={portal ?? undefined}
                offset={6}
                crossOffset={-6}
                className="ws-menu-pop"
              >
                <Menu
                  aria-label={`Download ${title}`}
                  className="ws-menu-list"
                  onAction={(key: Key) => run(key as Action)}
                >
                  {EXPORT_FORMATS.map((format) => (
                    <MenuItem
                      key={format.id}
                      id={`export:${format.id}`}
                      textValue={format.label}
                      className="ws-menu-row"
                    >
                      <FileDown className="ws-menu-row-icon" aria-hidden="true" />
                      <span className="ws-menu-row-label">{format.label}</span>
                    </MenuItem>
                  ))}
                </Menu>
              </Popover>
            </SubmenuTrigger>
          ) : null}
          {onArchive ? (
            <MenuItem id="lifecycle" textValue={archived ? "Restore" : "Archive"} className="ws-menu-row">
              {archived ? (
                <ArchiveRestore className="ws-menu-row-icon" aria-hidden="true" />
              ) : (
                <Archive className="ws-menu-row-icon" aria-hidden="true" />
              )}
              <span className="ws-menu-row-label">{archived ? "Restore" : "Archive"}</span>
            </MenuItem>
          ) : null}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
